import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { COMPONENT_CATALOG, DEFAULT_PLANE_FILTER, VIEWPORTS } from '../utils/defaults';

function PlaneFilterBar({ value, onChange }) {
  const mode = value?.mode || DEFAULT_PLANE_FILTER.mode;
  const isAll = mode === 'all';
  const isForeground = mode === 'foreground';
  const isBackground = mode === 'background';

  const select = (target) => {
    onChange({ mode: mode === target ? 'all' : target });
  };

  return (
    <div className="px-3 py-2 border-b border-border bg-muted/20 shrink-0">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
        Plans d&apos;affichage
      </p>
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => select('foreground')}
          className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium border transition-smooth ${
            isAll || isForeground
              ? 'bg-primary/10 border-primary/40 text-primary'
              : 'bg-background border-border text-muted-foreground opacity-50'
          } ${isForeground ? 'ring-1 ring-primary/40 shadow-sm' : ''}`}
          title="Premier plan — éléments au-dessus"
        >
          <Icon name="BringToFront" size={12} />
          Premier plan
        </button>
        <button
          type="button"
          onClick={() => select('background')}
          className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium border transition-smooth ${
            isAll || isBackground
              ? 'bg-primary/10 border-primary/40 text-primary'
              : 'bg-background border-border text-muted-foreground opacity-50'
          } ${isBackground ? 'ring-1 ring-primary/40 shadow-sm' : ''}`}
          title="Second plan — éléments en fond"
        >
          <Icon name="SendToBack" size={12} />
          Second plan
        </button>
      </div>
      {mode !== 'all' ? (
        <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug">
          {mode === 'foreground'
            ? 'Second plan atténué — seuls les éléments du premier plan sont sélectionnables.'
            : 'Premier plan atténué — les clics passent à travers pour sélectionner le second plan (même partiellement visible).'}
        </p>
      ) : null}
    </div>
  );
}

export default function EditorRightPanel({
  activeSectionId,
  hasMenu,
  viewMode = 'desktop',
  addViewportOnly = false,
  onAddViewportOnlyChange,
  onAdd,
  planeFilter,
  onPlaneFilterChange,
}) {
  const [collapsed, setCollapsed] = useState(false);

  if (collapsed) {
    return (
      <aside className="w-11 shrink-0 border-l border-border bg-card flex flex-col h-full items-center py-3 gap-2">
        <button
          type="button"
          className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-smooth"
          title="Afficher le panneau"
          onClick={() => setCollapsed(false)}
        >
          <Icon name="PanelLeftOpen" size={18} />
        </button>
        <div
          className="flex-1 flex items-center justify-center [writing-mode:vertical-rl] rotate-180"
          aria-hidden
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Composants
          </span>
        </div>
      </aside>
    );
  }

  return (
    <aside className="w-80 shrink-0 border-l border-border bg-card flex flex-col h-full min-h-0 transition-[width] duration-200">
      <div className="p-3 border-b border-border flex items-start gap-2 shrink-0">
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-foreground text-sm">Composants</h2>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            Ajoutez des éléments ou filtrez par plan d&apos;affichage.
          </p>
        </div>
        <button
          type="button"
          className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground shrink-0 transition-smooth"
          title="Replier le panneau"
          onClick={() => setCollapsed(true)}
        >
          <Icon name="PanelRightClose" size={18} />
        </button>
      </div>

      <PlaneFilterBar value={planeFilter} onChange={onPlaneFilterChange} />

      <div className="px-3 py-2 border-b border-border bg-muted/10 shrink-0">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={addViewportOnly}
            onChange={(e) => onAddViewportOnlyChange?.(e.target.checked)}
            className="mt-0.5 rounded border-border"
          />
          <span className="text-[11px] text-muted-foreground leading-snug">
            <span className="font-medium text-foreground">
              Uniquement pour {VIEWPORTS[viewMode]?.label || viewMode}
            </span>
            <br />
            Le composant ajouté n&apos;apparaîtra que sur cet écran (mobile, tablette ou ordinateur).
          </span>
        </label>
      </div>

      <div className="p-3 space-y-2 overflow-y-auto flex-1 min-h-0">
        {COMPONENT_CATALOG.map((item) => {
          const isSection = item.type.startsWith('section-');
          let disabled = false;
          let disabledReason = '';

          if (item.type === 'menu') {
            disabled = hasMenu;
            disabledReason = 'Un seul menu par page';
          } else if (!isSection) {
            disabled = !activeSectionId;
            disabledReason = 'Sélectionnez une section';
          }

          return (
            <button
              key={item.type}
              type="button"
              disabled={disabled}
              onClick={() => onAdd(item.type)}
              className={`w-full flex items-start gap-3 rounded-xl border p-3 text-left transition-smooth ${
                disabled
                  ? 'opacity-45 cursor-not-allowed border-border bg-muted/20'
                  : 'border-border hover:border-primary/40 hover:bg-muted/40 hover:shadow-sm'
              }`}
              title={disabled ? disabledReason : undefined}
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                  disabled ? 'bg-muted' : 'bg-primary/10'
                }`}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  className={disabled ? 'text-muted-foreground' : 'text-primary'}
                />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground">{item.label}</div>
                <div className="text-xs text-muted-foreground">
                  {disabled && item.type === 'menu' ? disabledReason : item.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </aside>
  );
}
